import React from "react";

// A decorative oversized glyph bled off a card's top-right corner, in the same
// construction the ClientDashboard KPI cards use: its own overflow-hidden layer
// so it clips to the card, low opacity, and never a pointer target.
//
// The parent must be positioned (`relative`). PanelShell already is; a bare card
// needs the class adding.
//
// Each glyph says what the card counts. They are decoration, not a legend — no
// labels, hidden from assistive tech, and never the only way to tell two cards
// apart.

export const ICONS = {
  // Calendar with a tick — a reservation that is confirmed.
  booked: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18M9 15l2 2 4-4" />
    </>
  ),
  // Handset — the agent said so, and nothing has confirmed it yet.
  agent: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />,
  // A record that already exists — a filed booking being asked about.
  existing: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M9 13h6M9 17h4" />
    </>
  ),
  // Banknote.
  revenue: (
    <>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 12h.01M18 12h.01" />
    </>
  ),
  // Moon — calls that landed when nobody was there.
  afterHours: <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />,
  // Turned back around.
  recovered: <path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5" />,
  // Hours given back.
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  // Speech bubble — attribution is the visitor's own words. The same bubble the
  // conversation rows use for their avatar, so the two read as one family.
  //
  // It doubles as the chat page's "the agent said so" glyph: `agent` is a
  // handset, and a handset on a page about website chat states something untrue
  // about where the conversation happened.
  quote: <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V5a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />,
};

const TONES = {
  brand: "text-[#32B1FF]",
  money: "text-emerald-500",
};

const CardWatermark = ({ icon, tone = "brand", size = 96 }) => {
  const glyph = ICONS[icon];
  if (!glyph) return null;
  return (
    <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none" aria-hidden="true">
      <div className={`absolute -top-5 -right-5 opacity-[0.13] dark:opacity-[0.09] ${TONES[tone] || TONES.brand}`}>
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {glyph}
        </svg>
      </div>
    </div>
  );
};

export default CardWatermark;
